// App.js
import React, { useEffect, useState, useCallback } from 'react'; // Add useCallback
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell, IconButton, Collapse, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Checkbox, FormControlLabel,
  FormGroup, Tooltip, Link, Stack, Tabs, Tab, Alert, Grid, MenuItem, CircularProgress, TablePagination // Added TablePagination
} from '@mui/material';
import { showGlobalNotification } from '../../context/NotificationContext.js';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon, KeyboardArrowUp as KeyboardArrowUpIcon, Check as CheckIcon,
  Close as CloseIcon, Delete as DeleteIcon, Refresh as RefreshIcon, Description as DescriptionIcon, Undo as UndoIcon, // Removed EditIcon
  Business as BusinessIcon, AttachMoney as AttachMoneyIcon, Assignment as AssignmentIcon,
  InsertDriveFile as InsertDriveFileIcon
} from '@mui/icons-material';
import W9Tab from './W9Tab';
import FacilitiesTab from './FacilitiesTab';
import RatesTab from './RatesTab';
import InsuranceTab from './InsuranceTab';
import BackgroundCheckTab from './BackgroundCheckTab'; // Import the new tab component
import { Resizable } from 're-resizable';
// Removed unused imports: ServicesField, CoveredZipsField, InsuranceCoverageField, BusinessInfoField, OwnershipField
import Step0Display from './Step0Display'; // Import the new component
import ScheduleInput from '../ScheduleInput'; // Import ScheduleInput
import { useLoading } from '../../context/LoadingContext'; // Correct the import path
import { deepMerge } from '../../utils/objectUtils'; // Import deepMerge from utility file
import { getDocumentUrl } from '../../utils/authUtils';
import { JobMapComponent } from '../JobMapComponent'; // Import the map component
// Removed unused imports: correctScheduleHandler, generateScheduleInput (handled differently now)

// Helper to ensure boolean values (fixes the prop type error for Checkbox).
// The public form stores billingSame as 'yes'/'no' strings, so accept those too.
const ensureBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const v = value.toLowerCase();
    return v === 'true' || v === 'yes';
  }
  return Boolean(value);
};

// "Other Information" at the bottom of the Application tab is a catch-all: it
// prints every application field that isn't listed here. Because it's a
// denylist, any field added to the schema shows up automatically — which is how
// raw JSON blobs and internal plumbing ended up on screen. Anything with its own
// tab, anything internal, and anything sensitive belongs in this list.
const OTHER_INFO_HIDDEN_FIELDS = new Set([
  // Identity / record plumbing
  '_id', 'step', '__v', 'vendorId', 'createdAt', 'updatedAt', 'status',
  'approvalDate', 'approvedBy',

  // Shown in the form above this section
  'companyName', 'ownerFirstName', 'ownerLastName', 'email', 'phoneCountry',
  'phoneCountryCode', 'phoneNumber', 'facilityCountry', 'facilityAddress1',
  'facilityAddress2', 'facilityCity', 'facilityState', 'facilityZip',
  'billingSame', 'billingAddress', 'bankingInfo', 'services', 'territories',
  'ownership', 'businessInfo', 'termsAgreement', 'codeOfConductAgreement',
  'employees', 'signature',

  // Each of these has a dedicated tab — dumping them here was pure noise
  'w9', 'w9Path', 'w9RequestDate', 'facilities', 'zipCodeFile',
  'insurance', 'insurancePolicies', 'coiRequestDate',
  'rateSheetPath', 'rateSheetSentDate',
  'backgroundCheck', 'backgroundCheckPath',

  // Document version history — browsable in the selector on each tab
  'w9History', 'coiHistory', 'rateSheetHistory',

  // Internal: hashed upload token and its expiry. Never useful on screen.
  'w9RequestToken', 'w9RequestTokenExpires',

  // Surfaced as a warning below instead of a bare label
  'w9NeedsReview'
]);

// Remove setIsLoading from props as it's handled globally now
function Apps({ step, searchQuery, advancedSearchCriteria, setError }) {
  const [applications, setApplications] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, applicationId: null, action: null });
  // Credentials of users created by a full approval — shown in a dialog since
  // email delivery can't be relied on
  const [credentialsDialog, setCredentialsDialog] = useState({ open: false, credentials: null });
  const [editingFields, setEditingFields] = useState({});
  const [vendorIds, setVendorIds] = useState({});
  const [activeTabs, setActiveTabs] = useState({}); // Track active tab for each application
  const [saveStatus, setSaveStatus] = useState({}); // Track save status for each application
  const { startLoading } = useLoading(); // Get startLoading
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // --- Deep Merge Utility removed, now imported ---

  // Define dispatch software options (same as in appformcomp.js)
  const dispatchSoftwareOptions = [
    'TRAXERO Dispatch Anywhere', 'Towbook', 'Bosch Towing Management', 'OctopusPro',
    'Roadside Protect Navigator', 'Agero Swoop', 'TowSoft', 'Omadi Core',
    'InTow Software', 'TOPS (Beacon)', 'VTS Cloud', 'Ranger SST', 'Other',
  ];

  // Define the list of boolean service keys based on the new schema
  const serviceKeys = [
    // Roadside Services
    'jumpStartLight', 'jumpStartMedium', 'jumpStartHeavy',
    'tireChangeLight', 'tireChangeMedium', 'tireChangeHeavy',
    'lockOutLight', 'lockOutMedium', 'lockOutHeavy',
    'fuelDeliveryLight', 'fuelDeliveryMedium', 'fuelDeliveryHeavy',
    'winchOutLight', 'winchOutMedium', 'winchOutHeavy',
    // Towing Services
    'lightDutyTowing', 'mediumDutyTowing', 'heavyDutyTowing',
    'accidentSceneTowing', 'secondaryTow',
    // Other Services
    'mobileMechanic', 'mediumHeavyTire', 'storageFacility',
    'keyMaking', 'locksmith', 'electricCharging'
  ];

  // Define all possible ownership keys based on the schema
  const ownershipKeys = [
    'familyOwned', 'womenOwned', 'minorityOwned', 'veteranOwned', 
    'lgbtqOwned', 'smallBusiness', 'disadvantagedBusiness'
  ];

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchApplications = useCallback(async (pageNum = 0, rowsPerPageNum = 10) => {
    const stopLoading = startLoading();
    if (setError) setError(null);
    try {
      let url = `${process.env.REACT_APP_API_URL}/api/applications?step=${step}&page=${pageNum + 1}&limit=${rowsPerPageNum}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (advancedSearchCriteria && Object.keys(advancedSearchCriteria).length > 0) {
        const params = new URLSearchParams();
        const addParams = (obj, prefix = '') => {
          Object.entries(obj).forEach(([key, value]) => {
            if (typeof value === 'object' && value !== null) addParams(value, prefix ? `${prefix}.${key}` : key);
            else if (value !== '' && value !== false && value !== null && value !== undefined) params.append(prefix ? `${prefix}.${key}` : key, value);
          });
        };
        addParams(advancedSearchCriteria);
        url += `&${params.toString()}`;
      }
      const response = await fetch(url, { headers: getAuthHeader() });
      if (!response.ok) throw new Error(`Failed to fetch applications: ${response.statusText}`);
      const data = await response.json();
      setApplications(data.applications || []);
      setTotalCount(data.totalCount || 0); // Assuming API returns totalCount
    } catch (error) {
      console.error('Error fetching applications:', error);
      if (setError) setError(error.message || 'Failed to load applications');
    } finally {
      stopLoading();
    }
  }, [startLoading, setError, step, searchQuery, advancedSearchCriteria]); // Added dependencies

  useEffect(() => {
    fetchApplications(page, rowsPerPage);

    // Add event listener for messages from the iframe
    const backendOrigin = process.env.NODE_ENV === 'production'
      ? (process.env.REACT_APP_API_URL || window.location.origin)
      : 'http://localhost:5000';
    const handleMessage = (event) => {
      // Check if the message is from our iframe and contains upload success data
      if (event.origin === backendOrigin && event.data && event.data.success) {
        console.log('Received success message from iframe:', event.data);
        const { filename, zipCodes } = event.data;
        const applicationId = event.data.applicationId; // Assuming backend sends appId back

        if (applicationId) {
          setApplications(prevApplications => {
            const updatedApplications = prevApplications.map(app => {
              if (app._id === applicationId) {
                // Update the specific application object with the new file info
                const updatedApp = {
                  ...app,
                  territories: {
                    ...app.territories,
                    zipCodeFile: filename,
                    // Optionally update zipCodes if sent back by backend
                    ...(zipCodes && { zipCodes: zipCodes.split(',').map(zip => zip.trim()).filter(zip => zip !== '') })
                  }
                };
                console.log('Updated application state with new zip file:', updatedApp);
                return updatedApp;
              }
              return app;
            });
            
            console.log('Applications state after zip file update:', updatedApplications);
            return updatedApplications;
          });
          
          // Refresh the applications from server after updating local state
          setTimeout(() => fetchApplications(page, rowsPerPage), 500);
        }
      } else if (event.origin === backendOrigin && event.data && event.data.error) {
         console.error('Received error message from iframe:', event.data.error);
         showGlobalNotification(`Zip code file upload failed: ${event.data.error}`, 'error');
      }
    };

    window.addEventListener('message', handleMessage);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('message', handleMessage);
    };

  }, [step, searchQuery, advancedSearchCriteria, fetchApplications, page, rowsPerPage]); // Added page, rowsPerPage

  // Updated handleSaveApplicationChanges
  const handleSaveApplicationChanges = async (applicationId) => {
    const stopLoading = startLoading();
    const application = applications.find(app => app._id === applicationId);
    if (!application) { stopLoading(); return; }
    const editsToApply = editingFields[applicationId] || {};
    let updatedApplication = deepMerge(application, editsToApply);

    // --- START: Add logic to process 'sameTimeSelectedDays' ---
    if (updatedApplication.services?.schedule?.sameTimeSelectedDays) {
      const schedule = updatedApplication.services.schedule;
      const openTime = schedule.selectedDaysOpen || '';
      const closeTime = schedule.selectedDaysClose || '';
      const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

      daysOfWeek.forEach(day => {
        if (schedule.days?.[day]?.isOpen) {
          // Only apply if the day is marked as open
          if (!schedule.days[day]) schedule.days[day] = {}; // Ensure day object exists
          schedule.days[day].open = openTime;
          schedule.days[day].close = closeTime;
        }
      });
      // Optional: Clear selectedDaysOpen/Close after applying, if desired
      // schedule.selectedDaysOpen = ''; 
      // schedule.selectedDaysClose = '';
    }
    // --- END: Add logic ---


    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${applicationId}`, {
        method: 'PUT', headers: getAuthHeader(), body: JSON.stringify(updatedApplication),
      });
      if (!response.ok) {
        let errorDetails = response.statusText;
        try { const errorData = await response.json(); errorDetails = errorData.message || errorDetails; } catch (parseError) {}
        throw new Error(`Failed to update application: ${errorDetails}`);
      }
      await response.json(); // Process the response body but don't assign to unused 'data'
      setSaveStatus(prev => ({ ...prev, [applicationId]: { success: 'Application changes saved successfully', error: null } }));
      await fetchApplications(page, rowsPerPage);
      setEditingFields((prev) => {
        const newState = { ...prev };
        delete newState[applicationId];
        return newState;
      });
    } catch (error) {
      console.error('Error updating application:', error);
      setSaveStatus(prev => ({ ...prev, [applicationId]: { success: null, error: error.message || 'Failed to save application changes' } }));
    } finally {
      stopLoading();
    }
  };

  const clearSaveStatus = (applicationId) => {
    setSaveStatus(prev => ({ ...prev, [applicationId]: null }));
  };

  const handleExpandClick = (applicationId) => {
    setExpandedRows((prev) => ({ ...prev, [applicationId]: !prev[applicationId] }));
    if (!activeTabs[applicationId]) setActiveTabs((prev) => ({ ...prev, [applicationId]: 0 }));
  };

  const handleTabChange = (applicationId, newValue) => {
    setActiveTabs((prev) => ({ ...prev, [applicationId]: newValue }));
  };

  const handleActionClick = (applicationId, action) => {
    setConfirmDialog({ open: true, applicationId, action });
  };

  const handleConfirmClose = () => {
    setConfirmDialog({ open: false, applicationId: null, action: null });
  };

  const createUserFromApplication = (application) => {
    const newUser = {
      vendorId: application.vendorId, email: application.email, companyName: application.companyName || '',
      ownerFirstName: application.ownerFirstName, ownerLastName: application.ownerLastName,
      facilityCountry: application.facilityCountry, facilityAddress1: application.facilityAddress1,
      facilityAddress2: application.facilityAddress2 || '', facilityCity: application.facilityCity,
      facilityState: application.facilityState, facilityZip: application.facilityZip,
      territories: { zipCodes: application.territories?.zipCodes || '' }, superAdmin: 1,
      username: application.vendorId, password: `${application.vendorId}123`
    };
    fetch(`${process.env.REACT_APP_API_URL}/api/users`, { method: 'POST', headers: getAuthHeader(), body: JSON.stringify(newUser) })
      .then(response => { if (!response.ok) return response.json().then(err => Promise.reject(err)); return response.json(); })
      .then(() => { fetchApplications(page, rowsPerPage); })
      .catch(error => { console.error('Error creating user:', error); });
  };

  const handleConfirmAction = async () => {
    const { applicationId, action } = confirmDialog;
    let newStep = null;
    const stopLoading = startLoading();
    try {
      if (action === 'delete') {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${applicationId}`, { method: 'DELETE', headers: getAuthHeader() });
        if (!response.ok) throw new Error('Failed to delete');
        await fetchApplications(page, rowsPerPage); handleConfirmClose(); stopLoading(); return;
      }
      const application = applications.find(app => app._id === applicationId);
      const currentStep = application ? application.step : -1;
      let response;

      if (action === 'approve') {
        if (currentStep === 0) { // New (0) -> In Review (1): simple step change, NO approval side effects
          console.log(`[Approve Action] Moving App ${applicationId} from New (Step 0) to In Review (Step 1)`);
          response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${applicationId}/step`, {
            method: 'PATCH',
            headers: getAuthHeader(),
            body: JSON.stringify({ step: 1 })
          });
        } else if (currentStep === 1) { // In Review (1) -> Approved (2): full approval (vendor ID, users, rate agreements)
          console.log(`[Approve Action] Approving App ${applicationId} from In Review (Step 1) to Approved (Step 2)`);
          response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${applicationId}/advance-to-review`, {
            method: 'PATCH', // Use PATCH for partial update (step + vendorId)
            headers: getAuthHeader()
            // No body needed, controller handles logic
          });
        } else if (currentStep === 2) { // Advance Step 2 -> Step 4 (Approved + Onboarding)
           console.log(`[Approve Action] Completing Onboarding for App ${applicationId} (Step 4)`);
           response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${applicationId}/complete-onboarding`, {
             method: 'POST', // Use POST for action with side effects (user creation)
             headers: getAuthHeader()
             // No body needed, controller handles logic
           });
        } else {
           console.warn(`[Approve Action] Approval action not applicable for current step ${currentStep}`);
           handleConfirmClose();
           stopLoading();
           return; // Exit if not step 0, 1, or 2
        }
      } else if (action === 'moveBack') {
        // Move the application back one step: In Review->New, Approved->In Review, Denied->In Review, Completed->Approved
        const prevStepMap = { 1: 0, 2: 1, 3: 1, 4: 2 };
        const prevStep = prevStepMap[currentStep];
        if (prevStep === undefined) {
          console.warn(`[Move Back Action] Cannot move back from step ${currentStep}`);
          handleConfirmClose();
          stopLoading();
          return;
        }
        console.log(`[Move Back Action] Moving App ${applicationId} back from Step ${currentStep} to Step ${prevStep}`);
        response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${applicationId}/step`, {
          method: 'PATCH',
          headers: getAuthHeader(),
          body: JSON.stringify({ step: prevStep })
        });
      } else if (action === 'deny') {
        // Deny action - update step to 3 (Denied)
        console.log(`[Deny Action] Denying App ${applicationId} (Step 3)`);
        response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${applicationId}`, {
            method: 'PUT', // Or PATCH if only updating step/status
            headers: getAuthHeader(),
            body: JSON.stringify({ step: 3, status: 'denied' })
        });
      } else {
         console.error(`[Confirm Action] Unknown action: ${action}`);
         handleConfirmClose();
         stopLoading();
         return;
      }

      // Process response for approve/deny actions
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${action} application`);
      }

      // Full approval returns the credentials of every user it created —
      // email delivery is unreliable, so show them in a window for the
      // approver to record and pass along.
      if (action === 'approve' && currentStep === 1) {
        const approvalResult = await response.json().catch(() => null);
        if (approvalResult?.credentials) {
          setCredentialsDialog({ open: true, credentials: approvalResult.credentials });
        }
      }

      await fetchApplications(page, rowsPerPage); // Refresh the list
      handleConfirmClose();
 
    } catch (error) {
      console.error('Error in confirm action:', error);
      if (setError) setError(error.message || 'Action failed');
      handleConfirmClose();
    } finally { stopLoading(); }
  };

  const formatLabelName = (key) => {
    const labelMap = {
      services: 'Services', territories: 'Covered Zips', insurance: 'Insurance Coverage', ownership: 'Ownership',
      vendorId: 'Vendor ID', businessInfo: 'Business Info', ownerFirstName: "Owner's First Name", ownerLastName: "Owner's Last Name",
      phoneNumber: 'Phone Number', facilityCountry: 'Facility Country', facilityAddress1: 'Facility Address1',
      facilityAddress2: 'Facility Address2', facilityCity: 'Facility City', facilityState: 'Facility State',
      facilityZip: 'Facility Zip', billingSame: 'Billing Same', w9Address: 'W9 Address', backupWithholding: 'Backup Withholding',
      employees: 'Employees', createdAt: 'Created At', phoneCountryCode: 'Phone Country Code',
      // Roadside Services
      jumpStartLight: 'Jump Start (Light Duty)', jumpStartMedium: 'Jump Start (Medium Duty)', jumpStartHeavy: 'Jump Start (Heavy Duty)',
      tireChangeLight: 'Tire Change (Light Duty)', tireChangeMedium: 'Tire Change (Medium Duty)', tireChangeHeavy: 'Tire Change (Heavy Duty)',
      lockOutLight: 'Lock Out (Light Duty)', lockOutMedium: 'Lock Out (Medium Duty)', lockOutHeavy: 'Lock Out (Heavy Duty)',
      fuelDeliveryLight: 'Fuel Delivery (Light Duty)', fuelDeliveryMedium: 'Fuel Delivery (Medium Duty)', fuelDeliveryHeavy: 'Fuel Delivery (Heavy Duty)',
      winchOutLight: 'Winch Out (Light Duty)', winchOutMedium: 'Winch Out (Medium Duty)', winchOutHeavy: 'Winch Out (Heavy Duty)',
      // Towing Services
      lightDutyTowing: 'Light Duty Towing', mediumDutyTowing: 'Medium Duty Towing', heavyDutyTowing: 'Heavy Duty Towing',
      accidentSceneTowing: 'Accident Scene Towing', secondaryTow: 'Secondary Tow',
      // Other Services
      mobileMechanic: 'Mobile Mechanic', mediumHeavyTire: 'Medium/Heavy Tire', storageFacility: 'Storage Facility',
      keyMaking: 'Key Making', locksmith: 'Locksmith', electricCharging: 'Electric Charging',
      // Ownership
      familyOwned: 'Family-Owned', womenOwned: 'Women-Owned', minorityOwned: 'Minority-Owned',
      veteranOwned: 'Veteran-Owned', lgbtqOwned: 'LGBTQ+-Owned', smallBusiness: 'Small Business', disadvantagedBusiness: 'Disadvantaged Business',
    };
    return labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
  };

  const handleFieldEdit = (applicationId, fieldPath, value) => {
    setEditingFields((prev) => {
      let fieldUpdate = {}; const keys = fieldPath.split('.'); let currentLevel = fieldUpdate;
      for (let i = 0; i < keys.length - 1; i++) { currentLevel[keys[i]] = {}; currentLevel = currentLevel[keys[i]]; }
      currentLevel[keys[keys.length - 1]] = value;
      const prevAppFields = prev[applicationId] || {};
      const updatedAppFields = deepMerge(prevAppFields, fieldUpdate);
      console.log('[Apps.js] updatedAppFields after deepMerge:', updatedAppFields); // Added log
      const newState = { ...prev, [applicationId]: updatedAppFields };
      return newState;
    });
  };

  const generateVendorId = async (application) => {
    const stateAbbr = application.facilityState ? application.facilityState.substring(0, 2).toUpperCase() : 'XX';
    try {
      const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/users/vendorIds`, { headers: getAuthHeader() });
      if (!userResponse.ok) throw new Error('Failed to fetch user vendorIds');
      const userData = await userResponse.json(); const existingUserVendorIds = userData.vendorIds || [];
      const appResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/maxVendorId`, { headers: getAuthHeader() });
      if (!appResponse.ok) throw new Error('Failed to fetch max vendorId from applications');
      const appData = await appResponse.json(); let newIdNumber = appData.maxIdNumber || 0;
      let vendorId;
      do { newIdNumber += 1; vendorId = `${stateAbbr}${String(newIdNumber).padStart(8, '0')}`; } while (existingUserVendorIds.includes(vendorId));
      setVendorIds((prev) => ({ ...prev, [application._id]: vendorId }));
      handleFieldEdit(application._id, 'vendorId', vendorId);
    } catch (error) { console.error('Error generating Vendor ID:', error); }
  };

  // Updated DocumentViewer to fetch and display owner's background check link and handle errors
  function DocumentViewer({ application }) {
    const [documentErrors, setDocumentErrors] = useState({});
    const [ownerBcPath, setOwnerBcPath] = useState(null);
    const [loadingBc, setLoadingBc] = useState(false);
    const [errorBc, setErrorBc] = useState(null);
    const [retryCount, setRetryCount] = useState({});
    const getAuthHeader = useCallback((contentType = 'application/json') => { // Added getAuthHeader here for isolation
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };
        if (contentType) {
          headers['Content-Type'] = contentType;
        }
        return headers;
      }, []);


    // Fetch owner's background check record when component mounts or application changes
    useEffect(() => {
      const fetchOwnerBc = async () => {
        if (!application?._id) return; // Only need appId

        setLoadingBc(true);
        setErrorBc(null);
        setOwnerBcPath(null); // Reset path

        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${application._id}/background-checks`, {
            headers: getAuthHeader(null) // Use getAuthHeader
          });
          if (!response.ok) {
            throw new Error(`Failed to fetch background checks: ${response.statusText}`);
          }
          const records = await response.json();
          // Find the record marked as the primary applicant
          // Find the record marked as the primary applicant
          const ownerRecord = records.find(record => record.isPrimaryApplicant === true);
          const ownerName = `${application.ownerFirstName} ${application.ownerLastName}`; // Define ownerName here

          if (ownerRecord && ownerRecord.documentPath) {
            setOwnerBcPath(ownerRecord.documentPath);
          } else {
             console.log(`No background check record found or no document path for owner: ${ownerName}`);
             // Optionally set an error or just leave path null
             // setErrorBc('Owner background check document not found.');
          }
        } catch (err) {
          console.error("Error fetching owner background check:", err);
          setErrorBc(err.message || 'Could not load owner background check.');
        } finally {
          setLoadingBc(false);
        }
      };

      fetchOwnerBc();
    }, [application?._id, application?.ownerFirstName, application?.ownerLastName]); // Dependencies


    const handleDocumentError = (docType) => {
      setDocumentErrors(prev => ({ ...prev, [docType]: true }));
      console.error(`Error loading document: ${docType}`);
    };
    
    const handleRetry = (docType) => {
      setDocumentErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[docType];
        return newErrors;
      });
      setRetryCount(prev => ({...prev, [docType]: (prev[docType] || 0) + 1}));
    };

    return (
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
        <Typography variant="h6" gutterBottom>Documents</Typography>
        {/* W9 Link */}
        {application.w9Path && (
          <Box display="flex" alignItems="center" mb={1} sx={{
            padding: '8px 12px',
            borderRadius: '4px'
          }}>
            <Typography variant="subtitle2" sx={{ mr: 2, minWidth: '150px', fontWeight: 'bold' }}>W9 Form:</Typography>
            {documentErrors.w9 ? (
              <Box display="flex" alignItems="center">
                <Typography color="error" sx={{ mr: 1 }}>Error loading file.</Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => handleRetry('w9')}
                >
                  Retry
                </Button>
              </Box>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<InsertDriveFileIcon fontSize="small" />}
                component={Link}
                href={getDocumentUrl(application.w9Path)}
                target="_blank"
                rel="noopener noreferrer"
                onError={() => handleDocumentError('w9')}
                sx={{ textTransform: 'none' }}
              >
                View W9 Document
              </Button>
            )}
          </Box>
        )}

        {/* Background Check Link - Enhanced with consistent styling */}
        <Box display="flex" alignItems="center" mb={1} sx={{
          padding: '8px 12px',
          borderRadius: '4px'
        }}>
          <Typography variant="subtitle2" sx={{ mr: 2, minWidth: '150px', fontWeight: 'bold' }}>
            Background Check (Owner):
          </Typography>
          {loadingBc ? (
            <CircularProgress size={20} />
          ) : errorBc ? (
            <Typography color="error" variant="body2">{errorBc}</Typography>
          ) : ownerBcPath ? (
            documentErrors.ownerBc ? (
              <Box display="flex" alignItems="center">
                <Typography color="error" sx={{ mr: 1 }}>Error loading file.</Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => handleRetry('ownerBc')}
                >
                  Retry
                </Button>
              </Box>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<InsertDriveFileIcon fontSize="small" />}
                component={Link}
                href={getDocumentUrl(ownerBcPath)}
                target="_blank"
                rel="noopener noreferrer"
                onError={() => handleDocumentError('ownerBc')}
                sx={{ textTransform: 'none' }}
              >
                View Background Check
              </Button>
            )
          ) : application.backgroundCheck?.path ? (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<InsertDriveFileIcon fontSize="small" />}
              component={Link}
              href={getDocumentUrl(application.backgroundCheck.path)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: 'none' }}
            >
              View Background Check (Legacy)
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Not provided
            </Typography>
          )}
        </Box>

        {/* COI Link */}
        {application.insurance?.coiPath && (
          <Box display="flex" alignItems="center" mb={1} sx={{
            padding: '8px 12px',
            borderRadius: '4px'
          }}>
            <Typography variant="subtitle2" sx={{ mr: 2, minWidth: '150px', fontWeight: 'bold' }}>Cert. of Insurance:</Typography>
            {documentErrors.coi ? (
              <Box display="flex" alignItems="center">
                <Typography color="error" sx={{ mr: 1 }}>Error loading file.</Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => handleRetry('coi')}
                >
                  Retry
                </Button>
              </Box>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<InsertDriveFileIcon fontSize="small" />}
                component={Link}
                href={getDocumentUrl(application.insurance.coiPath)}
                target="_blank"
                rel="noopener noreferrer"
                onError={() => handleDocumentError('coi')}
                sx={{ textTransform: 'none' }}
              >
                View Insurance Certificate
              </Button>
            )}
          </Box>
        )}

        {/* Zip Code File Link - Enhanced and always visible */}
        <Box display="flex" alignItems="center" mb={1} sx={{
          backgroundColor: '#f0f7ff',
          padding: '8px 12px',
          borderRadius: '4px',
          border: '1px solid #bbdefb'
        }}>
          <Typography variant="subtitle2" sx={{ mr: 2, minWidth: '150px', fontWeight: 'bold' }}>
            Zip Codes File:
          </Typography>
          {application.territories?.zipCodeFile ? (
            documentErrors.zipFile ? (
              <Box display="flex" alignItems="center">
                <Typography color="error" sx={{ mr: 1 }}>Error loading file.</Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => handleRetry('zipFile')}
                >
                  Retry
                </Button>
              </Box>
            ) : (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<InsertDriveFileIcon fontSize="small" />}
                component={Link}
                href={getDocumentUrl(application.territories.zipCodeFile)}
                target="_blank"
                rel="noopener noreferrer"
                onError={() => handleDocumentError('zipFile')}
                sx={{ textTransform: 'none' }}
              >
                View Zip Code File
              </Button>
            )
          ) : (
            <Typography variant="body2" color="text.secondary">
              No zip code file uploaded
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchApplications(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page
    fetchApplications(0, newRowsPerPage);
  };

  return (
    <Box mt={2}>
      {applications.length > 0 ? (
        <>
          <Table style={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '40px', border: '1px solid #ccc' }} />
                {[ 'Company Name', "Owner's First Name", "Owner's Last Name", 'Phone', 'Country', 'Address', 'City', 'State', 'Zip' ].map((header, index) => (
                  <TableCell key={index} style={{ overflow: 'hidden', border: '1px solid #ccc', padding: 0 }}>
                    <Resizable enable={{ right: true }} style={{ display: 'inline-block', width: '100%', borderRight: '1px solid #ccc', boxSizing: 'border-box' }} handleStyles={{ right: { width: '5px', background: 'transparent', cursor: 'col-resize' } }} handleComponent={{ right: (<div style={{ width: '5px', cursor: 'col-resize', borderRight: '2px solid #000' }} />) }}>
                      <div style={{ padding: '0 8px', borderLeft: '1px solid #ccc' }}>{header}</div>
                    </Resizable>
                  </TableCell>
                ))}
                <TableCell style={{ width: '120px', border: '1px solid #ccc' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.map((application) => (
                <React.Fragment key={application._id}>
                  <TableRow>
                    <TableCell style={{ width: '40px', border: '1px solid #ccc' }}>
                      <IconButton onClick={() => handleExpandClick(application._id)} aria-label="expand row" size="small">
                        {expandedRows[application._id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    {[ application.companyName || 'N/A', application.ownerFirstName, application.ownerLastName, application.phoneNumber, application.facilityCountry, application.facilityAddress1, application.facilityCity, application.facilityState, application.facilityZip ].map((cellData, idx) => (
                      <TableCell key={idx} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', border: '1px solid #ccc' }}>{cellData}</TableCell>
                    ))}
                    <TableCell style={{ width: '160px', border: '1px solid #ccc' }} align="right">
                      {application.step !== 3 && application.step !== 4 && (
                        <>
                          <Tooltip title={application.step === 0 ? 'Move to In Review' : application.step === 1 ? 'Approve (creates vendor ID and users)' : 'Complete onboarding'}>
                            <IconButton aria-label="approve" onClick={() => handleActionClick(application._id, 'approve')}><CheckIcon /></IconButton>
                          </Tooltip>
                          <Tooltip title="Deny">
                            <IconButton aria-label="deny" onClick={() => handleActionClick(application._id, 'deny')}><CloseIcon /></IconButton>
                          </Tooltip>
                        </>
                      )}
                      {application.step > 0 && (
                        <Tooltip title="Move back a step">
                          <IconButton aria-label="move back" onClick={() => handleActionClick(application._id, 'moveBack')}><UndoIcon /></IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton aria-label="delete" onClick={() => handleActionClick(application._id, 'delete')}><DeleteIcon /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0, border: 'none' }} colSpan={11}>
                      <Collapse in={expandedRows[application._id]} timeout="auto" unmountOnExit>
                        <Box margin={1}>
                          <Box display="flex" alignItems="center" mb={2}>
                            <Typography variant="body1" style={{ fontWeight: 'bold', marginRight: '8px' }}>Vendor ID:</Typography>
                            {application.step === 0 ? (
                              <>
                                <TextField variant="outlined" size="small" value={ editingFields[application._id]?.vendorId || vendorIds[application._id] || '' } disabled style={{ marginRight: '8px' }}/>
                                <Tooltip title="Generate Vendor ID"><IconButton size="small" onClick={() => generateVendorId(application)}><RefreshIcon /></IconButton></Tooltip>
                              </>
                            ) : ( <Typography variant="body1">{application.vendorId || editingFields[application._id]?.vendorId || 'N/A'}</Typography> )}
                          </Box>
                          <Box sx={{ width: '100%' }}>
                            {(application.step === 1 || application.step === 2 || application.step === 4) ? ( // Include Step 4 here
                              <>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                  <Tabs value={activeTabs[application._id] || 0} onChange={(e, newValue) => handleTabChange(application._id, newValue)} aria-label="application tabs">
                                    <Tab icon={<AssignmentIcon />} iconPosition="start" label="Application" />
                                    <Tab icon={<DescriptionIcon />} iconPosition="start" label="W9" />
                                    <Tab icon={<BusinessIcon />} iconPosition="start" label="Facilities" />
                                    <Tab icon={<AttachMoneyIcon />} iconPosition="start" label="Rates" />
                                    <Tab icon={<DescriptionIcon />} iconPosition="start" label="Insurance" />
                                    <Tab icon={<AssignmentIcon />} iconPosition="start" label="Background Check" /> {/* Add new tab */}
                                  </Tabs>
                                </Box>
                                {activeTabs[application._id] === 0 && (
                                  <Box sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                      <Button variant="contained" color="primary" onClick={() => handleSaveApplicationChanges(application._id)} disabled={application.step === 4 || !editingFields[application._id] || Object.keys(editingFields[application._id] || {}).length === 0}>Save Application Changes</Button>
                                    </Box>
                                    {saveStatus[application._id]?.success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => clearSaveStatus(application._id)}>{saveStatus[application._id].success}</Alert>}
                                    {saveStatus[application._id]?.error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => clearSaveStatus(application._id)}>{saveStatus[application._id].error}</Alert>}
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                      {/* Map Section */}
                                      <Box sx={{ flex: 1 }}> {/* Removed fixed height */}
                                        {(() => {
                                          // Construct locationData for the map from application facility details
                                          const facilityLocationData = {
                                            street: application.facilityAddress1,
                                            // street2: application.facilityAddress2, // Map component doesn't use street2 for geocoding
                                            city: application.facilityCity,
                                            state: application.facilityState,
                                            zip: application.facilityZip,
                                            country: application.facilityCountry,
                                            // Add coordinates if they exist directly on the application object (adjust path if needed)
                                            // location: application.facilityLocation?.location
                                          };
                                          console.log('[Apps.js] Rendering JobMapComponent with locationData:', facilityLocationData);
                                          return <JobMapComponent locationData={facilityLocationData} />;
                                        })()}
                                      </Box>
                                      {/* Contact/Address Section */}
                                      <Box sx={{ flex: 1 }}>
                                        <Stack spacing={1}>
                                          <Typography variant="h6" gutterBottom>Contact Information</Typography>
                                          <TextField fullWidth label="Company Name" value={(editingFields[application._id]?.companyName ?? application.companyName) || ''} onChange={(e) => handleFieldEdit(application._id, 'companyName', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                          <TextField fullWidth label="Owner's First Name" value={(editingFields[application._id]?.ownerFirstName ?? application.ownerFirstName) || ''} onChange={(e) => handleFieldEdit(application._id, 'ownerFirstName', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                          <TextField fullWidth label="Owner's Last Name" value={(editingFields[application._id]?.ownerLastName ?? application.ownerLastName) || ''} onChange={(e) => handleFieldEdit(application._id, 'ownerLastName', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                          <TextField fullWidth label="Email" type="email" value={(editingFields[application._id]?.email ?? application.email) || ''} onChange={(e) => handleFieldEdit(application._id, 'email', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                          <Grid container spacing={1}>
                                            <Grid item xs={12} sm={4}><TextField fullWidth label="Phone Country" value={(editingFields[application._id]?.phoneCountry ?? application.phoneCountry) || 'United States'} onChange={(e) => handleFieldEdit(application._id, 'phoneCountry', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/></Grid>
                                            <Grid item xs={12} sm={2}><TextField fullWidth label="Code" value={(editingFields[application._id]?.phoneCountryCode ?? application.phoneCountryCode) || '+1'} onChange={(e) => handleFieldEdit(application._id, 'phoneCountryCode', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/></Grid>
                                            <Grid item xs={12} sm={6}><TextField fullWidth label="Phone Number" type="tel" value={(editingFields[application._id]?.phoneNumber ?? application.phoneNumber) || ''} onChange={(e) => handleFieldEdit(application._id, 'phoneNumber', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/></Grid>
                                          </Grid>
                                          <Typography variant="h6" sx={{ mt: 2 }} gutterBottom>Facility Address</Typography>
                                          <TextField fullWidth label="Facility Country" value={(editingFields[application._id]?.facilityCountry ?? application.facilityCountry) || 'United States'} onChange={(e) => handleFieldEdit(application._id, 'facilityCountry', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                          <TextField fullWidth label="Facility Address Line 1" value={(editingFields[application._id]?.facilityAddress1 ?? application.facilityAddress1) || ''} onChange={(e) => handleFieldEdit(application._id, 'facilityAddress1', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                          <TextField fullWidth label="Facility Address Line 2" value={(editingFields[application._id]?.facilityAddress2 ?? application.facilityAddress2) || ''} onChange={(e) => handleFieldEdit(application._id, 'facilityAddress2', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                          <TextField fullWidth label="City" value={(editingFields[application._id]?.facilityCity ?? application.facilityCity) || ''} onChange={(e) => handleFieldEdit(application._id, 'facilityCity', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                          <TextField fullWidth label="State/Province" value={(editingFields[application._id]?.facilityState ?? application.facilityState) || ''} onChange={(e) => handleFieldEdit(application._id, 'facilityState', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                          <TextField fullWidth label="Zip/Postal Code" value={(editingFields[application._id]?.facilityZip ?? application.facilityZip) || ''} onChange={(e) => handleFieldEdit(application._id, 'facilityZip', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                          <Typography variant="h6" sx={{ mt: 2 }} gutterBottom>Billing Information</Typography>
                                          {/* FIX #2: Use ensureBoolean for billingSame checkbox */}
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={ensureBoolean(editingFields[application._id]?.billingSame ?? application.billingSame)}
                                                onChange={(e) => handleFieldEdit(application._id, 'billingSame', e.target.checked)}
                                                disabled={application.step === 4}
                                                size="small"
                                              />
                                            }
                                            label="Billing Same as Facility?"
                                            sx={{height: '30px'}}
                                          />
                                          {/* FIX #3: Use ensureBoolean for billingSame conditional */}
                                          {!ensureBoolean(editingFields[application._id]?.billingSame ?? application.billingSame) && (
                                            <Box sx={{ pl: 2, borderLeft: '2px solid #eee', mt: 1 }}>
                                              <Typography variant="subtitle2" gutterBottom>Billing Address</Typography>
                                              <TextField fullWidth label="Billing Address Line 1" value={(editingFields[application._id]?.billingAddress?.address1 ?? application.billingAddress?.address1) || ''} onChange={(e) => handleFieldEdit(application._id, 'billingAddress.address1', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                              <TextField fullWidth label="Billing City" value={(editingFields[application._id]?.billingAddress?.city ?? application.billingAddress?.city) || ''} onChange={(e) => handleFieldEdit(application._id, 'billingAddress.city', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                              <TextField fullWidth label="Billing State" value={(editingFields[application._id]?.billingAddress?.state ?? application.billingAddress?.state) || ''} onChange={(e) => handleFieldEdit(application._id, 'billingAddress.state', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                              <TextField fullWidth label="Billing Zip" value={(editingFields[application._id]?.billingAddress?.zip ?? application.billingAddress?.zip) || ''} onChange={(e) => handleFieldEdit(application._id, 'billingAddress.zip', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                            </Box>
                                          )}
                                          <Typography variant="h6" sx={{ mt: 2 }} gutterBottom>Banking Information (ACH)</Typography>
                                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Used to set up ACH payments to this service provider.
                                          </Typography>
                                          <TextField fullWidth label="Account Holder Name" value={(editingFields[application._id]?.bankingInfo?.accountHolderName ?? application.bankingInfo?.accountHolderName) || ''} onChange={(e) => handleFieldEdit(application._id, 'bankingInfo.accountHolderName', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                          <TextField fullWidth label="Bank Name" value={(editingFields[application._id]?.bankingInfo?.bankName ?? application.bankingInfo?.bankName) || ''} onChange={(e) => handleFieldEdit(application._id, 'bankingInfo.bankName', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                          <Grid container spacing={1}>
                                            <Grid item xs={12} sm={4}>
                                              <TextField fullWidth label="Routing Number" value={(editingFields[application._id]?.bankingInfo?.routingNumber ?? application.bankingInfo?.routingNumber) || ''} onChange={(e) => handleFieldEdit(application._id, 'bankingInfo.routingNumber', e.target.value.replace(/\D/g, '').slice(0, 9))} margin="dense" size="small" disabled={application.step === 4} inputProps={{ inputMode: 'numeric' }} helperText="9 digits"/>
                                            </Grid>
                                            <Grid item xs={12} sm={5}>
                                              <TextField fullWidth label="Account Number" value={(editingFields[application._id]?.bankingInfo?.accountNumber ?? application.bankingInfo?.accountNumber) || ''} onChange={(e) => handleFieldEdit(application._id, 'bankingInfo.accountNumber', e.target.value.replace(/\D/g, '').slice(0, 17))} margin="dense" size="small" disabled={application.step === 4} inputProps={{ inputMode: 'numeric' }}/>
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                              <TextField select fullWidth label="Account Type" value={(editingFields[application._id]?.bankingInfo?.accountType ?? application.bankingInfo?.accountType) || ''} onChange={(e) => handleFieldEdit(application._id, 'bankingInfo.accountType', e.target.value)} margin="dense" size="small" disabled={application.step === 4}>
                                                <MenuItem value="checking">Checking</MenuItem>
                                                <MenuItem value="savings">Savings</MenuItem>
                                              </TextField>
                                            </Grid>
                                          </Grid>
                                          <Typography variant="h6" sx={{ mt: 2 }} gutterBottom>Services</Typography>
                                          <FormGroup>
                                            {serviceKeys.map((key) => (
                                              <FormControlLabel
                                                key={key}
                                                control={
                                                  /* FIX #6: Use ensureBoolean for service checkboxes */
                                                  <Checkbox
                                                    checked={ensureBoolean(editingFields[application._id]?.services?.[key] ?? application.services?.[key] ?? false)}
                                                    onChange={(e) => handleFieldEdit(application._id, `services.${key}`, e.target.checked)}
                                                    disabled={application.step === 4}
                                                    name={key}
                                                    size="small"
                                                  />
                                                }
                                                label={formatLabelName(key)}
                                                sx={{ height: '30px' }}
                                              />
                                            ))}
                                          </FormGroup>
                                          {(() => {
                                            // Directly merge application data and editingFields for scheduleDataPropValue
                                            // Ensure open247 is explicitly set from editingFields or application data
                                            const scheduleDataPropValue = {
                                              ...deepMerge(application.services?.schedule ?? {}, editingFields[application._id]?.services?.schedule ?? {}),
                                              open247: editingFields[application._id]?.services?.open247 ?? application.services?.open247 ?? false
                                            };

                                            // Debug the application step and disabled state
                                            console.log(`[Apps.js] Application step: ${application.step}, disabled state: ${application.step === 4}`);
                                            
                                            // Debug the current editing fields for this application
                                            console.log('[Apps.js] Current editingFields for this application:', editingFields[application._id]);

                                            const handleScheduleInputChange = (fieldPath, value) => {
                                              console.log(`[Apps.js] handleScheduleInputChange called with fieldPath: ${fieldPath}, value:`, value);
                                              let fullPath;
                                              if (fieldPath === 'open247') {
                                                fullPath = 'services.open247';
                                              } else {
                                                fullPath = `services.schedule.${fieldPath}`;
                                              }
                                              console.log(`[Apps.js] Setting field at path: ${fullPath} for application ${application._id}`);
                                              handleFieldEdit(application._id, fullPath, value);
                                              
                                              // Log the updated editing fields after the change
                                              setTimeout(() => {
                                                console.log('[Apps.js] Updated editingFields after change:',
                                                  editingFields[application._id]?.services?.schedule);
                                              }, 0);
                                            };

                                            console.log('[Apps.js] scheduleDataPropValue before rendering ScheduleInput:', scheduleDataPropValue); // Added log
                                            
                                            // Remove the key prop that causes complete re-rendering and state loss
                                            return (
                                              <ScheduleInput
                                                scheduleData={scheduleDataPropValue}
                                                onChange={handleScheduleInputChange}
                                                disabled={application.step === 4}
                                              />
                                            );
                                          })()}
                                          <Typography variant="h6" sx={{ mt: 2 }} gutterBottom>Territories</Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                            <TextField
                                              fullWidth
                                              multiline
                                              label="Covered Zip Codes (Manual Entry)"
                                              value={(editingFields[application._id]?.territories?.zipCodes ?? application.territories?.zipCodes) || ''}
                                              onChange={(e) => handleFieldEdit(application._id, 'territories.zipCodes', e.target.value)}
                                              margin="dense"
                                              size="small"
                                              disabled={application.step === 4}
                                            />
                                            
                                            {/* Create a direct form for uploading zip code files */}
                                            <form
                                              action={`${process.env.REACT_APP_API_URL}/api/applications/${application._id}/upload-zip-file`}
                                              method="POST"
                                              encType="multipart/form-data"
                                              target="upload_target"
                                              style={{ display: 'flex', alignItems: 'center' }}
                                            >
                                              <input
                                                type="hidden"
                                                name="token"
                                                value={localStorage.getItem('token')}
                                              />
                                              <input
                                                type="file"
                                                name="zipCodeFile"
                                                accept=".txt,.csv"
                                                style={{ display: 'none' }}
                                                id={`zip-file-upload-${application._id}`}
                                                onChange={(e) => {
                                                  if (e.target.files && e.target.files[0]) {
                                                    console.log('File selected:', e.target.files[0].name);
                                                    // Auto-submit the form when a file is selected
                                                    e.target.form.submit();
                                                  }
                                                }}
                                              />
                                              <Button
                                                variant="contained"
                                                component="label"
                                                size="small"
                                                sx={{ mt: 1, minWidth: '140px' }}
                                                disabled={application.step === 4}
                                                htmlFor={`zip-file-upload-${application._id}`}
                                              >
                                                Upload Zip File
                                              </Button>
                                            </form>
                                            
                                            {/* Hidden iframe for form target */}
                                            <iframe
                                              title="Upload target"
                                              name="upload_target"
                                              style={{ display: 'none' }}
                                              onLoad={() => {
                                                // Refresh the application data after upload
                                                fetchApplications(page, rowsPerPage);
                                              }}
                                            ></iframe>
                                          </Box>
                                          {application.territories?.zipCodeFile && (
                                            <Box sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              mb: 1,
                                              backgroundColor: '#f0f7ff',
                                              padding: '8px 12px',
                                              borderRadius: '4px',
                                              border: '1px solid #bbdefb'
                                            }}>
                                              <Typography variant="body2" sx={{ mr: 1, fontWeight: 'bold' }}>Current file:</Typography>
                                              <Button
                                                variant="outlined"
                                                color="primary"
                                                size="small"
                                                startIcon={<InsertDriveFileIcon fontSize="small" />}
                                                component={Link}
                                                href={getDocumentUrl(application.territories.zipCodeFile)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ textTransform: 'none' }}
                                              >
                                                View Zip Code File
                                              </Button>
                                            </Box>
                                          )}
                                          <Typography variant="h6" sx={{ mt: 2 }} gutterBottom>Business Ownership</Typography>
                                          <FormGroup>
                                            {/* Iterate over all defined ownership keys */}
                                            {ownershipKeys.map((key) => {
                                              // Safely access current and edited values, defaulting to false if undefined
                                              const currentVal = application.ownership?.[key] ?? false;
                                              const editedVal = editingFields[application._id]?.ownership?.[key];
                                              // Prioritize edited value if it exists, otherwise use current value
                                              const valueToCheck = editedVal !== undefined ? editedVal : currentVal;
                                              return (
                                                <FormControlLabel
                                                  key={key}
                                                  control={
                                                    <Checkbox
                                                      checked={ensureBoolean(valueToCheck)}
                                                      onChange={(e) => handleFieldEdit(application._id, `ownership.${key}`, e.target.checked)}
                                                      disabled={application.step === 4}
                                                      size="small"
                                                    />
                                                  }
                                                  label={formatLabelName(key)}
                                                  sx={{height: '30px'}}
                                                />
                                              );
                                            })}
                                          </FormGroup>
                                          <Typography variant="h6" sx={{ mt: 2 }} gutterBottom>Signature</Typography>
                                          <TextField fullWidth label="Electronic Signature" value={(editingFields[application._id]?.signature ?? application.signature) || ''} onChange={(e) => handleFieldEdit(application._id, 'signature', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                          <Typography variant="h6" sx={{ mt: 2 }} gutterBottom>Employees</Typography>
                                          <TextField fullWidth label="Number of W2 Employees (above 1 requires Workers' Comp)" type="number" value={(editingFields[application._id]?.employees ?? application.employees) || ''} onChange={(e) => handleFieldEdit(application._id, 'employees', e.target.value)} margin="dense" size="small" disabled={application.step === 4}/>
                                          <Typography variant="h6" sx={{ mt: 2 }} gutterBottom>Business Info</Typography>
                                          <TextField select fullWidth label="Years in Business" value={(editingFields[application._id]?.businessInfo?.yearsInBusiness ?? application.businessInfo?.yearsInBusiness) || ''} onChange={(e) => handleFieldEdit(application._id, 'businessInfo.yearsInBusiness', e.target.value)} margin="dense" size="small" disabled={application.step === 4}>
                                              {['Less than a year', '1-5 years', '5-10 years', 'More than 10 years'].map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
                                          </TextField>
                                          <TextField select fullWidth label="EV Experience" value={(editingFields[application._id]?.businessInfo?.electricVehicleExp ?? application.businessInfo?.electricVehicleExp) || ''} onChange={(e) => handleFieldEdit(application._id, 'businessInfo.electricVehicleExp', e.target.value)} margin="dense" size="small" disabled={application.step === 4}>
                                              <MenuItem value="yes">Yes</MenuItem><MenuItem value="no">No</MenuItem>
                                          </TextField>
                                           <TextField select fullWidth label="Digital Dispatch" value={(editingFields[application._id]?.businessInfo?.digitalDispatch ?? application.businessInfo?.digitalDispatch) || ''} onChange={(e) => handleFieldEdit(application._id, 'businessInfo.digitalDispatch', e.target.value)} margin="dense" size="small" disabled={application.step === 4}>
                                              <MenuItem value="yes">Yes</MenuItem><MenuItem value="no">No</MenuItem>
                                          </TextField>
                                          {/* Conditionally render Dispatch Software dropdown */}
                                          { (editingFields[application._id]?.businessInfo?.digitalDispatch ?? application.businessInfo?.digitalDispatch) === 'yes' && (
                                            <TextField
                                              select
                                              fullWidth
                                              label="Dispatch Software Used"
                                              value={(editingFields[application._id]?.businessInfo?.dispatchSoftware ?? application.businessInfo?.dispatchSoftware) || ''}
                                              onChange={(e) => handleFieldEdit(application._id, 'businessInfo.dispatchSoftware', e.target.value)}
                                              margin="dense"
                                              disabled={application.step === 4}
                                              size="small"
                                            >
                                              {dispatchSoftwareOptions.map((software) => (
                                                <MenuItem key={software} value={software}>
                                                  {software}
                                                </MenuItem>
                                              ))}
                                            </TextField>
                                          )}
                                          {/* Add Other Motorclub Services fields */}
                                          <TextField
                                            select
                                            fullWidth
                                            label="Other Motorclub Services?"
                                            value={(editingFields[application._id]?.businessInfo?.otherMotorclubServices ?? application.businessInfo?.otherMotorclubServices) || ''}
                                            onChange={(e) => handleFieldEdit(application._id, 'businessInfo.otherMotorclubServices', e.target.value)}
                                            margin="dense"
                                            disabled={application.step === 4}
                                            size="small"
                                          >
                                            <MenuItem value="yes">Yes</MenuItem>
                                            <MenuItem value="no">No</MenuItem>
                                          </TextField>
                                          { (editingFields[application._id]?.businessInfo?.otherMotorclubServices ?? application.businessInfo?.otherMotorclubServices) === 'yes' && (
                                            <TextField
                                              fullWidth
                                              label="List Other Motorclubs"
                                              value={(editingFields[application._id]?.businessInfo?.otherMotorclubsList ?? application.businessInfo?.otherMotorclubsList) || ''}
                                              onChange={(e) => handleFieldEdit(application._id, 'businessInfo.otherMotorclubsList', e.target.value)}
                                              margin="dense"
                                              disabled={application.step === 4}
                                              size="small"
                                              multiline
                                              rows={2} // Adjust rows as needed
                                            />
                                          )}
                                          <Typography variant="h6" sx={{ mt: 2 }} gutterBottom>Agreements</Typography>
                                          {/* FIX #5: Use ensureBoolean for agreement checkboxes */}
                                          <FormControlLabel
                                            control={ <Checkbox checked={ensureBoolean(application.termsAgreement)} disabled size="small"/> }
                                            label="Terms Agreement Signed" sx={{height: '30px'}}
                                          />
                                          <FormControlLabel
                                            control={ <Checkbox checked={ensureBoolean(application.codeOfConductAgreement)} disabled size="small"/> }
                                            label="Code of Conduct Signed" sx={{height: '30px'}}
                                          />
                                          <Box mt={2}>
                                            <Typography variant="subtitle2" gutterBottom>Other Information</Typography>
                                            {application.w9NeedsReview && (
                                              <Alert severity="warning" sx={{ mb: 1 }}>
                                                This W-9 was uploaded without the emailed verification link — confirm it belongs to this applicant on the W9 tab.
                                              </Alert>
                                            )}
                                            {Object.entries(application).map(([key, value]) => {
                                              if (OTHER_INFO_HIDDEN_FIELDS.has(key)) return null;
                                              if (value === null || value === undefined || value === '') return null;
                                              if (typeof value === 'boolean') return value ? <Typography key={key} variant="body2">{formatLabelName(key)}</Typography> : null;
                                              // Anything still reaching here is an unrecognized field. Show that it
                                              // exists without dumping a wall of JSON into the page.
                                              if (typeof value === 'object') {
                                                const count = Array.isArray(value) ? value.length : Object.keys(value).length;
                                                if (!count) return null;
                                                return (
                                                  <Box key={key} mb={1}>
                                                    <Typography variant="body2" color="textSecondary" fontWeight="bold">{formatLabelName(key)}:</Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                      {Array.isArray(value) ? `${count} item${count === 1 ? '' : 's'}` : `${count} field${count === 1 ? '' : 's'}`}
                                                    </Typography>
                                                  </Box>
                                                );
                                              }
                                              return (<Box key={key} mb={1}><Typography variant="body2" color="textSecondary" fontWeight="bold">{formatLabelName(key)}:</Typography><Typography variant="body2">{String(value)}</Typography></Box>);
                                            })}
                                          </Box>
                                        </Stack>
                                      </Box>
                                    </Box>
                                    <DocumentViewer application={application} />
                                  </Box>
                                )}
                                {activeTabs[application._id] === 1 && <Box sx={{ p: 2 }}><W9Tab application={application} editingFields={editingFields} handleFieldEdit={handleFieldEdit} handleSaveChanges={() => handleSaveApplicationChanges(application._id)} onUpdate={() => fetchApplications(page, rowsPerPage)} isReadOnly={application.step === 4} /></Box>}
                                {activeTabs[application._id] === 2 && <Box sx={{ p: 2 }}><FacilitiesTab application={application} editingFields={editingFields} handleFieldEdit={handleFieldEdit} handleSaveChanges={() => handleSaveApplicationChanges(application._id)} isReadOnly={application.step === 4} /></Box>}
                                {activeTabs[application._id] === 3 && <Box sx={{ p: 2 }}><RatesTab application={application} editingFields={editingFields} handleFieldEdit={handleFieldEdit} handleSaveChanges={() => handleSaveApplicationChanges(application._id)} isReadOnly={application.step === 4} /></Box>}
                                {activeTabs[application._id] === 4 && <Box sx={{ p: 2 }}><InsuranceTab application={application} onUpdate={() => fetchApplications(page, rowsPerPage)} isReadOnly={application.step === 4} /></Box>}
                                {/* Add rendering for the new tab */}
                                {activeTabs[application._id] === 5 && <Box sx={{ p: 2 }}><BackgroundCheckTab application={application} editingFields={editingFields} handleFieldEdit={handleFieldEdit} handleSaveChanges={() => handleSaveApplicationChanges(application._id)} isReadOnly={application.step === 4} /></Box>}
                              </>
                            ) : application.step === 0 ? (
                               <Step0Display application={application} />
                            ) : application.step === 3 ? ( // Explicitly handle Step 3 (Denied)
                              <Box sx={{ p: 2 }}>
                                <Typography variant="h6" color="error">Application Denied</Typography>
                                <DocumentViewer application={application} />
                              </Box>
                            ) : null} {/* Handle other steps (or null if none) */}
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </>
      ) : (
        <Typography variant="body1" align="center">No applications found.</Typography>
      )}
      <Dialog open={confirmDialog.open} onClose={handleConfirmClose} aria-labelledby="confirm-dialog-title">
        <DialogTitle id="confirm-dialog-title">{confirmDialog.action === 'approve' ? 'Advance Application' : confirmDialog.action === 'deny' ? 'Deny Application' : confirmDialog.action === 'moveBack' ? 'Move Application Back' : 'Delete Application'}</DialogTitle>
        <DialogContent>{(() => {
          if (confirmDialog.action === 'approve') {
            const app = applications.find(a => a._id === confirmDialog.applicationId);
            if (app?.step === 0) return 'Move this application to In Review?';
            if (app?.step === 1) return 'Approve this application? This will generate a vendor ID, create portal users, and send welcome emails — the new users’ credentials will also be shown to you after approval.';
            return 'Complete onboarding for this application?';
          }
          if (confirmDialog.action === 'deny') return 'Are you sure you want to deny this application?';
          if (confirmDialog.action === 'moveBack') return 'Move this application back one step? Users or agreements already created will not be removed.';
          return 'Are you sure you want to delete this application? This action cannot be undone.';
        })()}</DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose} color="primary">Cancel</Button>
          <Button onClick={handleConfirmAction} color="primary" autoFocus>Confirm</Button>
        </DialogActions>
      </Dialog>
      {/* Credentials of users created by a full approval. Email delivery is
          unreliable, and the owner's registration token is shown ONLY here —
          it cannot be recovered after this dialog is closed. */}
      <Dialog open={credentialsDialog.open} maxWidth="sm" fullWidth disableEscapeKeyDown onClose={(e, reason) => { if (reason !== 'backdropClick') setCredentialsDialog({ open: false, credentials: null }); }}>
        <DialogTitle>Application Approved — User Credentials</DialogTitle>
        <DialogContent>
          {credentialsDialog.credentials && (
            <Box>
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                Save these now. The registration token is not stored anywhere else and cannot be recovered after this window is closed.
              </Typography>
              <Typography variant="subtitle2">{credentialsDialog.credentials.companyName} — Vendor ID: {credentialsDialog.credentials.vendorId}</Typography>
              {credentialsDialog.credentials.owner && (
                <Box sx={{ mt: 2, p: 1.5, border: '1px solid #ccc', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Owner (SP): {credentialsDialog.credentials.owner.name}</Typography>
                  <Typography variant="body2">Email: {credentialsDialog.credentials.owner.email}</Typography>
                  <Typography variant="body2">Username: {credentialsDialog.credentials.owner.username}</Typography>
                  {credentialsDialog.credentials.owner.registrationToken && (
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>Registration Token: <strong>{credentialsDialog.credentials.owner.registrationToken}</strong></Typography>
                  )}
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>{credentialsDialog.credentials.owner.note}</Typography>
                </Box>
              )}
              {(credentialsDialog.credentials.employees || []).map((emp, idx) => (
                <Box key={idx} sx={{ mt: 2, p: 1.5, border: '1px solid #ccc', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Employee: {emp.name}</Typography>
                  <Typography variant="body2">Email: {emp.email}</Typography>
                  <Typography variant="body2">Username: {emp.username}</Typography>
                  <Typography variant="body2">Temporary Password: <strong>{emp.tempPassword}</strong></Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>{emp.note}</Typography>
                </Box>
              ))}
              {(credentialsDialog.credentials.employees || []).length === 0 && !credentialsDialog.credentials.owner && (
                <Typography variant="body2" sx={{ mt: 2 }}>No new users were created by this approval.</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            const c = credentialsDialog.credentials;
            if (!c) return;
            const lines = [`${c.companyName} — Vendor ID: ${c.vendorId}`];
            if (c.owner) {
              lines.push('', `Owner (SP): ${c.owner.name}`, `Email: ${c.owner.email}`, `Username: ${c.owner.username}`);
              if (c.owner.registrationToken) lines.push(`Registration Token: ${c.owner.registrationToken}`, 'Register at the /register page with the Vendor ID and this token (valid 24 hours).');
              if (c.owner.note) lines.push(c.owner.note);
            }
            (c.employees || []).forEach(emp => {
              lines.push('', `Employee: ${emp.name}`, `Email: ${emp.email}`, `Username: ${emp.username}`, `Temporary Password: ${emp.tempPassword}`, emp.note || '');
            });
            navigator.clipboard.writeText(lines.join('\n'));
          }}>Copy All</Button>
          <Button onClick={() => setCredentialsDialog({ open: false, credentials: null })} color="primary" variant="contained">I've Saved These</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Apps;
